import {Injectable} from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import {BehaviorSubject, Observable, Subject, throwError, EMPTY, defer, from} from 'rxjs';
import {catchError, filter, retry, switchMap, take, tap} from 'rxjs/operators';
import {Router} from '@angular/router';

import * as Sentry from '@sentry/angular'; // ✅ SENTRY: Import Sentry

import {AppConstant} from '../utility/app-constant';
import {LoginLogoutService} from './auth/login-logout.service';
import {TimeOutService} from './auth/time-out.service';
import {NotificationService} from './notification/notification.service';
import {HttpResponseMessage} from '../utility/http-response-message';
import {PrivilegeService} from './privilege.service';
import {UserService} from './user/user.service';

@Injectable({
  providedIn: 'root'
})
export class BasicAuthHttpInterceptorServiceService implements HttpInterceptor {
  private isHandlingDelicateError = false;
  private pendingRequests: Subject<any> = new Subject<any>();
  private delicateErrorSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private statusToTrack = [AppConstant.BAD_REQUEST, AppConstant.NOT_FOUND, AppConstant.SERVER_TIME_OUT, AppConstant.PAPERTRL_SERVER_ERROR];

  constructor(
    private router: Router,
    private tokenRevalidateService: TimeOutService,
    public privilegeService: PrivilegeService,
    private loginLogOutService: LoginLogoutService,
    public notificationService: NotificationService,
    private userService: UserService
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.privilegeService.networkStatus) {
      return throwError('Network not available');
    }

    if (this.isHandlingDelicateError && !req.url.includes('sec_get_user_details_and_nav_list')) {
      return EMPTY;
    }

    req = this.addHeaders(req);

    // ✅ SENTRY: Add breadcrumb for outgoing request
    Sentry.addBreadcrumb({
      category: 'http',
      message: `${req.method} ${req.url}`,
      level: 'info',
      data: {
        method: req.method,
        url: req.url,
      }
    });

    return next.handle(req).pipe(
      retry(0),
      catchError((error: HttpErrorResponse) => this.handleError(error, req, next))
    );
  }

  private addHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const headers: { [name: string]: string } = {};
    const addHeader = (key: string, value: string | null) => {
      if (value) {
        headers[key] = value;
      }
    };

    if (localStorage.getItem(AppConstant.ACCESS_TOKEN)) {
      addHeader('Authorization', `Bearer ${localStorage.getItem(AppConstant.ACCESS_TOKEN)}`);
    }
    if (localStorage.getItem(AppConstant.SESSION_ID)) {
      addHeader('X-Auth-Token', localStorage.getItem(AppConstant.SESSION_ID));
    }
    if (localStorage.getItem(AppConstant.CSRF_TOKEN)) {
      addHeader('X-CSRF-TOKEN', localStorage.getItem(AppConstant.CSRF_TOKEN));
    }
    if (localStorage.getItem(AppConstant.SUB_CLIENT_ID) && this.privilegeService.isPortal()) {
      addHeader('SUB-CLIENT-ID', localStorage.getItem(AppConstant.SUB_CLIENT_ID));
    }

    const user = JSON.parse(localStorage.getItem(AppConstant.SESSION_USER_ATTR));
    if (user?.switchedToDelegateAccount && !req.url.includes('sec_get_user_details_and_nav_list') && !req.url.includes('sec_return_to_profile')) {
      addHeader('Delegate-owner', user.delegateUserDetails.username);
    }

    return req.clone({setHeaders: headers});
  }

    private handleError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      let errorMessage = error.error instanceof ErrorEvent ? error.error.error : error.error;

    // ✅ SENTRY: Capture error for failed API requests (ignore known ones like token refresh)
    if (!req.url.includes('oauth/token') && this.statusToTrack.includes(error.status)) {

      const apiError = new Error(`[API ERROR (${error.status.toString()})] ${req.method} ${req.url}`);
      apiError.name = 'API Error';

      Sentry.captureException(apiError, {
        tags: {
          errorType: 'api-error',
          url: req.url,
          method: req.method,
          status: error.status.toString(),
        },
        extra: {
          request: {
            url: req.url,
            method: req.method,
            body: req.body,
            headers: req.headers.keys().reduce((acc, key) => {
              acc[key] = req.headers.get(key);
              return acc;
            }, {} as Record<string, string>),
          },
          response: {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            error: error.error,
          }
        }
      });
    }

    switch (error.status) {
      case AppConstant.FORBIDDEN:
        this.notificationService.errorMessage(HttpResponseMessage.FORBIDDEN_MESSAGE);
        break;
      case AppConstant.DELICATE_NO_ACCESS:
        return this.handleDelicateAccessError(req, next, error);
      case AppConstant.SERVER_DOWN:
      case AppConstant.SERVER_NOT_FOUND:
      case AppConstant.SERVER_TIME_OUT:
        this.notificationService.errorMessage(HttpResponseMessage.SERVER_DOWN_MESSAGE);
        errorMessage = HttpResponseMessage.SERVER_DOWN_MESSAGE;
        break;
      case AppConstant.UN_AUTHORIZED:
        return this.handle401Error(req, next);
      case AppConstant.BAD_REQUEST:
        if (error.url.includes('oauth/token')) {
          this.loginLogOutService.logOut(true);
        }
        break;
      case AppConstant.NOT_ACCEPTABLE:
        if (!req.url.includes('/oauth/token')) {
          this.loginLogOutService.logOut(true);
        }
        break;
    }

    return throwError(errorMessage);
  }

  private handleDelicateAccessError(req: HttpRequest<any>, next: HttpHandler, errorRes: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (!this.isHandlingDelicateError || req.url.includes('sec_get_user_details_and_nav_list')) {
      this.isHandlingDelicateError = true;
      this.delicateErrorSubject.next(true);
      const user = JSON.parse(localStorage.getItem(AppConstant.SESSION_USER_ATTR));
      const id = (errorRes?.error.id ?? '');
      const username = (errorRes?.error.username ?? '');
      let toDelicate = user && this.privilegeService.isSwitchedToDelicate() ? AppConstant.STATUS_NO : AppConstant.STATUS_YES;
      if (this.privilegeService.getCurrentDelicateUserDetails() && errorRes.error.username !== this.privilegeService.getCurrentDelicateUserDetails().username) {
        toDelicate = AppConstant.STATUS_YES;
      }
      const promise = this.getUserDetailsAndNavList(id, toDelicate, username);

      return defer(() =>
        from(promise.then(async (res: any) => {
          this.isHandlingDelicateError = false;
          this.delicateErrorSubject.next(false);
          await this.tokenRevalidateService.setAuthenticatedDelegateAccount(res.body.navContainerDto, res.body.loginUser, errorRes);
          return null;
        })).pipe(
          switchMap(() => EMPTY),
          catchError((error) => {
            this.isHandlingDelicateError = false;
            this.delicateErrorSubject.next(false);
            return throwError(error);
          })
        )
      );
    } else {
      return EMPTY;
    }
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.url.includes('oauth/token') && localStorage.getItem(AppConstant.REFRESH_TOKEN)) {
      if (!this.tokenRevalidateService.refreshToken) {
        this.refreshTokenSubject.next(null);
        this.tokenRevalidateService.refreshToken = true;

        return this.tokenRevalidateService.refreshAccessToken().pipe(
          catchError(error => {
            this.tokenRevalidateService.refreshToken = false;
            this.loginLogOutService.logOut(true);
            return throwError(error);
          }),
          switchMap((res: any) => {
            const token = res.body.access_token;
            if (token) {
              this.refreshTokenSubject.next(token);
              this.tokenRevalidateService.refreshToken = false;
              this.tokenRevalidateService.setLoginProperties(res, true, true);
              return next.handle(this.addTokenHeader(req, token));
            }
            this.loginLogOutService.logOut(true);
          })
        );
      }
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addTokenHeader(req, token)))
      );
    } else {
      this.loginLogOutService.logOut(true);
    }
  }

  private addTokenHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({headers: req.headers.set('Authorization', 'Bearer ' + token)});
  }

  private getUserDetailsAndNavList(id: string, toDelegate: string, username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService.getUserDetailsAndNavList(id, toDelegate, username).subscribe(
        res => resolve(res),
        error => reject(error)
      );
    });
  }
}
